import {
  type BaseEditor,
  Editor,
  Element,
  Node,
  Path,
  Range,
  Transforms,
} from 'slate'

export const LIST_TYPES = ['bulleted-list', 'numbered-list']

export function withNormalisedLists<T extends BaseEditor>(editor: T) {
  const e = editor as T & Editor & { listsPluginInstalled: true }
  const { normalizeNode, deleteFragment, insertBreak } = e

  e.listsPluginInstalled = true

  e.normalizeNode = (entry) => {
    const [node, path] = entry

    if (!Element.isElement(node)) {
      normalizeNode(entry)
      return
    }

    if (LIST_TYPES.includes(node.type)) {
      // Check for nested lists
      for (const [child, childPath] of Node.children(e, path)) {
        // wrap nested things in list items
        if (!Element.isElement(child) || child.type !== 'list-item') {
          Transforms.wrapNodes(
            e,
            { type: 'list-item', children: [] },
            { at: childPath },
          )
          // After transforming, return early to re-normalize
          return
        }
      }
      return
    }

    if (node.type === 'list-item') {
      // Check for list items with incorrect children
      for (const [child, childPath] of Node.children(e, path)) {
        if (!Element.isElement(child)) continue

        if (child.type === 'list-item') {
          // lift nested list items
          Transforms.liftNodes(e, {
            at: childPath,
          })
          // After transforming, return early to re-normalize
          return
        }

        if (!Editor.isInline(e, child) && !LIST_TYPES.includes(child.type)) {
          // unwrap invalid children
          Transforms.unwrapNodes(e, {
            at: childPath,
          })
          // After transforming, return early to re-normalize
          return
        }
      }

      // if the list item is not a child of a list, unwrap it
      const [parent] = Editor.parent(e, path)
      if (!Element.isElement(parent) || !LIST_TYPES.includes(parent.type)) {
        Transforms.unwrapNodes(e, {
          at: path,
        })
      }
      return
    }

    // Proceed with the original normalization
    normalizeNode(entry)
  }

  e.deleteFragment = (options) => {
    const { selection } = e

    deleteFragment(options)

    // Lists often leave artifacts. Replace empty list fragments with paragraph
    if (Node.string(e).trim() === '') {
      Transforms.removeNodes(e, { at: [0] })
      Transforms.insertNodes(
        e,
        { type: 'paragraph', children: [{ text: '' }] },
        { at: [0] },
      )

      // Move the selection to the start of the new paragraph
      if (selection) {
        Transforms.select(e, Editor.start(e, [0]))
      }

      return
    }
  }

  e.insertBreak = () => {
    const { selection } = e

    // Proceed only if the selection is collapsed
    if (!selection || !Range.isCollapsed(selection)) {
      return insertBreak()
    }

    // Check if we're inside a list item
    const [listItemEntry] = Editor.nodes(e, {
      match: (n) => Element.isElement(n) && n.type === 'list-item',
    })

    if (!listItemEntry) {
      return insertBreak()
    }

    // Store the path of the current list item before inserting break
    const [listItem, currentPath] = listItemEntry

    // Get the parent list node
    const [listNode, listPath] = Editor.parent(e, currentPath)

    // Check if new list item is the last item in the list
    const isLastItemInList =
      currentPath[currentPath.length - 1] === listNode.children.length - 1

    if (isLastItemInList && Node.string(listItem).trim() === '') {
      if (listNode.children.length === 1) {
        // only item — remove the whole list
        Transforms.removeNodes(e, { at: listPath })
      } else {
        Transforms.removeNodes(e, { at: currentPath })
      }
      // add a new paragraph after the list
      Transforms.insertNodes(
        e,
        { type: 'paragraph', children: [{ text: '' }] },
        { at: Path.next(listPath) },
      )
      // and focus that paragraph
      Transforms.select(e, Path.next(listPath))
      return
    }

    // Insert a break (creates a new list item)
    insertBreak()
  }

  return e
}
